import { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

const THRESHOLD = 80;

const PullToRefresh = ({ children }) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const canPull = useCallback(() => {
    return window.scrollY <= 0;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (!canPull()) return;
      startY.current = e.touches[0].clientY;
      setPulling(true);
    };

    const onTouchMove = (e) => {
      if (!pulling || refreshing) return;
      if (!canPull()) {
        setPulling(false);
        setPullDistance(0);
        return;
      }
      const diff = e.touches[0].clientY - startY.current;
      if (diff > 0) {
        // Dampen the pull distance
        setPullDistance(Math.min(diff * 0.4, 120));
      }
    };

    const onTouchEnd = () => {
      if (!pulling) return;
      if (pullDistance >= THRESHOLD && !refreshing) {
        setRefreshing(true);
        setPullDistance(50);
        // Reload the page
        setTimeout(() => {
          window.location.reload();
        }, 400);
      } else {
        setPullDistance(0);
      }
      setPulling(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [pulling, pullDistance, refreshing, canPull]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  return (
    <div ref={containerRef} className="relative">
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center overflow-hidden transition-all pointer-events-none z-50"
        style={{
          height: pullDistance > 5 ? `${pullDistance}px` : '0px',
          top: 0,
          transition: pulling ? 'none' : 'height 0.3s ease',
        }}
      >
        <div
          className={`w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center ${
            refreshing ? 'animate-spin' : ''
          }`}
          style={{
            opacity: Math.min(progress * 1.5, 1),
            transform: `rotate(${progress * 360}deg)`,
          }}
        >
          <RefreshCw
            className={`w-5 h-5 ${progress >= 1 ? 'text-[#FF9980]' : 'text-gray-400'}`}
          />
        </div>
      </div>
      {/* Content shifted down */}
      <div
        style={{
          transform: pullDistance > 5 ? `translateY(${pullDistance}px)` : 'none',
          transition: pulling ? 'none' : 'transform 0.3s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
